import { useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY_MS = 350;

/**
 * Returns `value` delayed by `delayMs`, so a filter fed by a text input fires
 * one request per pause rather than one per keystroke.
 *
 * The setState sits in the timeout callback, not the effect body — the shape
 * react-hooks/set-state-in-effect allows.
 */
export function useDebouncedValue<T>(value: T, delayMs = DEFAULT_DELAY_MS): T {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(value), delayMs);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, delayMs]);

  return debounced;
}
